import React from "react";
import FormField, { SelectInput, TextInput } from "../common/FormField";

const LABELS = {
  eq: "равно",
  ne: "не равно",
  in: "одно из",
  not_in: "ни одно из",
  contains: "содержит",
  gt: "больше",
  gte: "не меньше",
  lt: "меньше",
  lte: "не больше",
  exists: "задано",
  missing: "не задано",
};
function supported(node, depth = 0) {
  if (!node || typeof node !== "object" || Array.isArray(node) || depth > 16)
    return false;
  const keys = Object.keys(node);
  if (keys.length === 1) {
    if ("always" in node) return typeof node.always === "boolean";
    if ("process_required" in node)
      return typeof node.process_required === "string";
    if ("not" in node) return supported(node.not, depth + 1);
    for (const op of ["all", "any"])
      if (op in node)
        return (
          Array.isArray(node[op]) &&
          node[op].length <= 100 &&
          node[op].every((n) => supported(n, depth + 1))
        );
  }
  return (
    typeof node.path === "string" &&
    typeof node.op === "string" &&
    !!LABELS[node.op] &&
    keys.every((k) => ["path", "op", "value", "values"].includes(k)) &&
    (!("values" in node) || Array.isArray(node.values))
  );
}
export function describeRule(node, paths = {}) {
  if (!node) return "Условие не задано";
  if (!supported(node))
    return "Условие сохранено без изменений; его конструкция пока не поддерживается редактором";
  if ("always" in node) return node.always ? "Всегда" : "Никогда";
  if ("process_required" in node)
    return `Нужен процесс ${node.process_required}`;
  if ("not" in node) return `НЕ (${describeRule(node.not, paths)})`;
  for (const op of ["all", "any"])
    if (op in node)
      return (
        "(" +
        node[op]
          .map((n) => describeRule(n, paths))
          .join(op === "all" ? " И " : " ИЛИ ") +
        ")"
      );
  return `${paths[node.path]?.[0]?.title || node.path} ${LABELS[node.op] || node.op} ${node.values?.join(", ") || (node.value ?? "")}`.trim();
}

export default function RuleEditor({
  value = { always: true },
  onChange,
  paths = {},
  choices = {},
  depth = 0,
}) {
  if (!supported(value))
    return (
      <div className="classifier-error" role="alert">
        <p>
          Конструкция сохранена без изменений. Публикация заблокирована до
          исправления. Редактор не преобразует её автоматически.
        </p>
        <button
          type="button"
          className="ap-button ap-button--secondary"
          onClick={() => onChange({ always: true })}
        >
          Явно заменить условием «Всегда»
        </button>
      </div>
    );
  const mode =
    "always" in value
      ? "always"
      : "all" in value
        ? "all"
        : "any" in value
          ? "any"
          : "not" in value
            ? "not"
            : "process_required" in value
              ? "process"
              : "attribute";
  const attrs = paths[value.path] || [],
    attr = attrs[0],
    number = attrs.some((a) => ["number", "integer"].includes(a.value_type));
  const allowed = [...new Set(attrs.flatMap((a) => a.values || []))];
  const pathKeys = Object.keys(paths).sort();
  const operators = [
    "eq",
    "ne",
    "in",
    "not_in",
    ...(attrs.some((a) => /set|array/.test(a.value_type)) ? ["contains"] : []),
    ...(number ? ["gt", "gte", "lt", "lte"] : []),
    "exists",
    "missing",
  ];
  const setMode = (m) =>
    onChange(
      m === "always"
        ? { always: true }
        : ["all", "any"].includes(m)
          ? { [m]: [value] }
          : m === "not"
            ? { not: value }
            : m === "process"
              ? { process_required: choices.process?.[0]?.id || "" }
              : { path: pathKeys[0] || "", op: "eq", value: "" },
    );
  const setPath = (p) =>
    onChange({ path: p, op: "eq", value: paths[p]?.[0]?.values?.[0] || "" });
  const setOp = (op) =>
    onChange({
      path: value.path,
      op,
      ...(["exists", "missing"].includes(op)
        ? {}
        : ["in", "not_in"].includes(op)
          ? { values: [] }
          : { value: allowed[0] || (number ? 0 : "") }),
    });
  const inputValue = (v) => (number ? Number(v) : v);
  return (
    <div className="classifier-rule">
      <FormField label={depth ? "Группа условия" : "ЕСЛИ"}>
        <SelectInput
          aria-label={`Вид условия ${depth}`}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value="always">Безусловно</option>
          <option value="attribute">Значение атрибута</option>
          <option value="process">Нужен процесс</option>
          {depth < 15 && (
            <>
              <option value="all">Все условия (И)</option>
              <option value="any">Любое условие (ИЛИ)</option>
              <option value="not">Отрицание (НЕ)</option>
            </>
          )}
        </SelectInput>
      </FormField>
      {mode === "always" && (
        <SelectInput
          aria-label="Безусловное значение"
          value={String(value.always)}
          onChange={(e) => onChange({ always: e.target.value === "true" })}
        >
          <option value="true">Всегда выполняется</option>
          <option value="false">Никогда не выполняется</option>
        </SelectInput>
      )}
      {mode === "process" && (
        <FormField label="Процесс">
          <SelectInput
            value={value.process_required}
            onChange={(e) => onChange({ process_required: e.target.value })}
          >
            {(choices.process || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} ({p.id})
              </option>
            ))}
          </SelectInput>
        </FormField>
      )}
      {["all", "any"].includes(mode) && (
        <div className="classifier-stack">
          {value[mode].map((child, i) => (
            <div className="classifier-rule-child" key={i}>
              <RuleEditor
                value={child}
                paths={paths}
                choices={choices}
                depth={depth + 1}
                onChange={(n) =>
                  onChange({
                    [mode]: value[mode].map((v, j) => (i === j ? n : v)),
                  })
                }
              />
              {value[mode].length > 1 && (
                <button
                  type="button"
                  className="ap-button ap-button--secondary"
                  onClick={() =>
                    onChange({ [mode]: value[mode].filter((_, j) => i !== j) })
                  }
                >
                  Убрать условие {i + 1}
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="ap-button ap-button--secondary"
            onClick={() =>
              onChange({ [mode]: [...value[mode], { always: true }] })
            }
          >
            Добавить условие
          </button>
        </div>
      )}
      {mode === "not" && (
        <RuleEditor
          value={value.not}
          paths={paths}
          choices={choices}
          depth={depth + 1}
          onChange={(not) => onChange({ not })}
        />
      )}
      {mode === "attribute" && (
        <div className="classifier-form-grid">
          <FormField label="Атрибут">
            <SelectInput
              value={value.path}
              onChange={(e) => setPath(e.target.value)}
            >
              {!paths[value.path] && (
                <option value={value.path}>
                  {value.path} — определение отсутствует
                </option>
              )}
              {pathKeys.map((p) => (
                <option key={p} value={p}>
                  {paths[p][0].title} ({p})
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Сравнение">
            <SelectInput
              value={value.op}
              onChange={(e) => setOp(e.target.value)}
            >
              {operators.map((op) => (
                <option key={op} value={op}>
                  {LABELS[op]}
                </option>
              ))}
            </SelectInput>
          </FormField>
          {!["exists", "missing"].includes(value.op) &&
            (["in", "not_in"].includes(value.op) ? (
              <FormField label="Значения (по одному в строке)">
                <textarea
                  className="ap-control"
                  value={(value.values || []).join("\n")}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      values: e.target.value
                        .split("\n")
                        .filter(Boolean)
                        .map(inputValue),
                    })
                  }
                />
              </FormField>
            ) : (
              <FormField label="Значение">
                {allowed.length ? (
                  <SelectInput
                    value={value.value ?? ""}
                    onChange={(e) =>
                      onChange({ ...value, value: e.target.value })
                    }
                  >
                    <option value="">Выберите значение</option>
                    {allowed.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </SelectInput>
                ) : (
                  <TextInput
                    type={number ? "number" : "text"}
                    value={value.value ?? ""}
                    onChange={(e) =>
                      onChange({ ...value, value: inputValue(e.target.value) })
                    }
                  />
                )}
              </FormField>
            ))}
          {attr?.value_type?.startsWith("derived") && (
            <p className="classifier-muted">
              Значение вычисляется из профиля, а не вводится вручную.
            </p>
          )}
        </div>
      )}
      {depth === 0 && (
        <p className="classifier-rule-summary">
          <strong>Правило:</strong> {describeRule(value, paths)}
        </p>
      )}
    </div>
  );
}
